import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import { GradientIcon } from '../gradients/GradientIcon';
import { GradientTypography } from '../gradients/GradientTypography';
import { LeaderboardScore, Scorer } from './LeaderboardScore';
import { Competition } from 'functions/src/types/firestore/Game/Competition';
import { Link } from '../Link';
import { useEffect, useMemo, useState } from 'react';
import { toKebabCase } from '../../util/string';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { usePrizeFormat } from 'src/hooks/usePrizeFormat';
import { ImageOverlayed } from '../ImageOverlayed';
import { Required } from 'utility-types';
import { useMobile } from 'src/hooks/useMobile';
import { useNumberFormatter } from 'src/hooks/useNumberFormatter';
import { useDateFormatter } from 'src/hooks/useDateFormatter';

const SX_ROW_GROUP = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  height: '100%',
};

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

export type LeaderboardProps = Required<
  Pick<
    Competition<Date> & { type: 'leaderboard' },
    | 'title'
    | 'gameTitle'
    | 'competitorCount'
    | 'endpoint'
    | 'imgUrl'
    | 'prize'
    | 'date'
  >,
  'imgUrl'
> & {
  noDivider?: boolean;
};

export const Leaderboard = ({
  title,
  gameTitle,
  competitorCount,
  endpoint,
  imgUrl,
  prize,
  date,
  noDivider = false,
}: LeaderboardProps) => {
  const format = useDateFormatter({ dateFormat: DATE_FORMAT, separator: ', ' });
  const prizeFormatted = usePrizeFormat(prize);
  const dateFormatted = useMemo(() => format(date), [format, date]);

  const [endpointData, setEndpointData] = useState<Scorer[]>([]);
  useEffect(() => {
    const handler = async () => {
      const response = await fetch(`${endpoint}?number=0`);
      const data = await response.json();
      if (!data || !data?.leaderBoard) {
        return;
      }
      const top = data.leaderBoard.slice(0, 3).map((item) => {
        const { guild, id, pfp, prestige, prize, rank, username, ...rest } =
          item;
        const [points] = Object.entries(rest);

        return {
          name: username,
          id: id,
          prize: prize,
          rank: rank,
          points: points[1],
          avatar: pfp,
          guild,
          prestige,
        };
      });
      setEndpointData(top);
    };
    if (endpoint) {
      handler();
    }
  }, [endpoint]);
  const theme = useTheme();

  const getLeaderboardLink = () => {
    const searchParam = new URLSearchParams();
    searchParam.append('leaderboard', title);
    return `/game/${toKebabCase(
      gameTitle,
    )}/leaderboards?${searchParam.toString()}`;
  };

  const link = getLeaderboardLink();

  const competitorCountFormatted = useNumberFormatter({
    notation: 'compact',
    maximumSignificantDigits: 3,
  }).format(competitorCount);

  const isMobile = useMobile();

  const render = (
    <>
      <ListItem sx={{ py: 2, pl: 2, pr: 0 }}>
        <ListItemAvatar>
          <ImageOverlayed src={imgUrl} width={124} height={124}>
            <Typography
              variant="body2"
              align="center"
              color="text.primary"
              sx={{
                textTransform: 'uppercase',
                fontWeight: 'bold',
                textShadow: '0px 0px 10px black',
              }}
            >
              {title}
            </Typography>
          </ImageOverlayed>
        </ListItemAvatar>
        <Grid container sx={{ justifyContent: 'space-between' }}>
          <Grid item sx={{ mr: isMobile ? 0 : 4, ml: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
              }}
            >
              <Box sx={SX_ROW_GROUP}>
                <Typography
                  variant="h6"
                  color={theme.palette.text.primary}
                  sx={{ textTransform: 'uppercase' }}
                >
                  {title}
                </Typography>
              </Box>
              <Box sx={SX_ROW_GROUP}>
                <Typography
                  variant="body1"
                  color={theme.palette.text.secondary}
                  sx={{ textTransform: 'capitalize' }}
                >
                  {gameTitle}
                </Typography>
              </Box>
              <Box sx={SX_ROW_GROUP}>
                <Typography
                  variant="body1"
                  color={theme.palette.text.secondary}
                >{`${competitorCountFormatted} competing`}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item sx={{ mb: 1 }}>
            <LeaderboardScore
              scorers={endpointData?.length > 0 ? endpointData : []}
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background:
                  'linear-gradient(145deg, rgba(5, 49, 89, 0.8) 50%, rgba(11, 65, 110, 0.8) 50%)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  pl: 3,
                }}
              >
                <GradientIcon
                  IconComponent={EmojiEventsIcon}
                  gradientColor="secondary.vertical"
                />
                <GradientTypography
                  variant="subtitle1"
                  gradientColor="secondary.horizontal"
                  sx={{ mx: 1 }}
                >
                  {`${prizeFormatted} Prize Pool`}
                </GradientTypography>
              </Box>
              <Box sx={{ pr: 6 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'text.secondary' }}
                >
                  {dateFormatted}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </ListItem>
      {!noDivider ? (
        <Divider
          sx={{ boxShadow: 1 }}
          orientation="horizontal"
          component="li"
        />
      ) : null}
    </>
  );
  return (
    <Link href={link} sx={{ textDecoration: 'none' }}>
      {render}
    </Link>
  );
};
